import React, { useState, useEffect } from 'react';
import { BRAND_NAME } from '../theme/brand';
import {
    Box,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Card,
    CardBody,
    Divider,
    Tag,
    VStack,
    HStack,
    Button
} from '@chakra-ui/react';
import { CopyIcon, CheckIcon } from '@chakra-ui/icons';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-json';

const SMSOnlyAPIPanel = ({ list }) => {
    const [copiedSection, setCopiedSection] = useState('');

    useEffect(() => {
        Prism.highlightAll();
    }, []);

    const smsRequestPayload = `{
  "to": "923xxxxxxxx14",
  "from": "YourSenderID",
  "text": "Your message text here"
}`;

    const smsSuccessResponse = `{
  "statusCode": 200,
  "status": "accepted",
  "messageid": "5e8c48b3-540a-4f43-ac8a-cc2a6a2114d6",
  "accepttime": 1727247788
}`;

    const smsErrorResponse = `{
  "status": "error",
  "code": 400,
  "message": "Invalid phone number format"
}`;

    const copyToClipboard = (text, section) => {
        navigator.clipboard.writeText(text);
        setCopiedSection(section);
        setTimeout(() => setCopiedSection(''), 2000);
    };

    const CodeBlock = ({ code, section, language = 'json' }) => (
        <Card bg="black" color="white" position="relative" p={4}>
            <CardBody p={0}>
                <Button
                    position="absolute"
                    top={3}
                    right={3}
                    size="sm"
                    variant="ghost"
                    colorScheme="whiteAlpha"
                    onClick={() => copyToClipboard(code, section)}
                    zIndex={10}
                >
                    {copiedSection === section ? <CheckIcon w={4} h={4} /> : <CopyIcon w={4} h={4} />}
                </Button>
                <Box
                    as="pre"
                    className={`language-${language}`}
                    style={{
                        backgroundColor: 'black',
                        textShadow: 'none',
                        color: 'white',
                        padding: '0px',
                        borderRadius: '8px',
                        margin: 0
                    }}
                >
                    <code className={`language-${language}`}>{code}</code>
                </Box>
            </CardBody>
        </Card>
    );

    const headers = [
        {
            header: "x-api-key",
            value: list.api_key,
            description: "Your unique API authentication key",
            isApiKey: true
        },
        {
            header: "Content-Type",
            value: "application/json",
            description: "Request body format",
            isApiKey: false
        }
    ];

    const payloadFields = [
        {
            field: "to",
            type: "string",
            required: true,
            description: "Recipient phone number (international format)"
        },
        {
            field: "from",
            type: "string",
            required: true,
            description: "Sender ID"
        },
        {
            field: "text",
            type: "string",
            required: true,
            description: "Message body"
        }
    ];

    const responseCodes = [
        {
            code: "200",
            status: "Success",
            description: "Message sent successfully",
            color: "green"
        },
        {
            code: "400",
            status: "Bad Request",
            description: "Invalid request payload or missing required fields ( Variable Missing )",
            color: "red"
        },
        {
            code: "401",
            status: "Unauthorized",
            description: "Invalid or missing API key",
            color: "red"
        },
        {
            code: "403",
            status: "Forbidden",
            description: "Insufficient permissions or account suspended",
            color: "red"
        },
        {
            code: "404",
            status: "Not Found",
            description: "Template or customer ID not found (Phone Number Missing,Country Not Allowed,Account Inactive,Duplicate Message,Invalid Template,Auth Failed)",
            color: "red"
        },
        {
            code: "429",
            status: "Rate Limit",
            description: "Too many requests - rate limit exceeded",
            color: "orange"
        },
        {
            code: "500",
            status: "Server Error",
            description: "Internal server error - please retry",
            color: "red"
        },
        {
            code: "503",
            status: "Service Unavailable",
            description: "Service temporarily unavailable - maintenance or high load",
            color: "orange"
        }
    ];

    const otherInfo = [
        {
            key: "message-id",
            description: "An auto generated unique 36 alpha numeric string"
        },
        {
            key: "accepttime",
            description: "The time we received and accepted the message request"
        }
    ];

    const notes = [
        "The phone number must be provided in international format without the + sign. For example, 923181018154 for a Pakistani number.",
        "The sender ID (from) should be your registered sender identifier."
    ];

    return (
        <Box>
            {/* Overview Section */}
            <Box my="20px">
                <Text fontSize="20px" fontWeight="600" color="#000000" mb="10px" >
                    Overview
                </Text>
                <Divider my={2} />
                <Card bg="#eaf3fdeb" borderRadius="lg" p={4} border="1px solid" borderColor="var(--brand-tint-strong)">
                    <Text fontSize="14px" color="#000000" >
                        The SMS Message Only API allows sending SMS/text notifications to a specific recipient using {BRAND_NAME}'s messaging gateway.
                    </Text>
                </Card>
            </Box>

            {/* Headers Section */}
            <Box mb="20px">
                <Text fontSize="20px" fontWeight="600" color="#000000" mb="10px" >
                    Headers
                </Text>
                <Divider my={2} />
                <TableContainer
                    width="100%"
                    bg="white"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    overflow="hidden"
                >
                    <Table variant="simple" width="100%">
                        <Thead bg="#f7f7f7">
                            <Tr>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Header
                                </Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Value
                                </Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Description
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody fontSize="14px">
                            {headers.map((item, index) => (
                                <Tr key={index} _hover={{ bg: "gray.50" }} transition="background 0.2s">
                                    <Td color="black" fontWeight="500" py={4} >
                                        {item.header}


                                    </Td>
                                    <Td color="black" py={4} >
                                        {item.value}
                                        {item.isApiKey && (
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                This is your API Key
                                            </Text>
                                        )}
                                    </Td>
                                    <Td color="black" py={4}>
                                        {item.description}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Request Payload Section */}
            <Box mb="20px">
                <Text fontSize="20px" fontWeight="600" color="#000000" mb="10px" >
                    Request Payload
                </Text>
                <Divider my={2} />
                <CodeBlock code={smsRequestPayload} section="sms-request" />
            </Box>

            {/* Payload Fields Section */}
            <Box mb="20px">
                <Text fontSize="20px" fontWeight="600" color="#000000" mb="10px" >
                    Payload Fields
                </Text>
                <Divider my={2} />
                <TableContainer
                    width="100%"
                    bg="white"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    overflow="hidden"
                >
                    <Table variant="simple" width="100%">
                        <Thead bg="#f7f7f7">
                            <Tr>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Field
                                </Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Type
                                </Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Required
                                </Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Description
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody fontSize="14px">
                            {payloadFields.map((field, index) => (
                                <Tr key={index} _hover={{ bg: "gray.50" }}>
                                    <Td color="black" fontWeight="500" py={4} >
                                        {field.field}
                                    </Td>
                                    <Td color="black" py={4}>
                                        {field.type}
                                    </Td>
                                    <Td py={4}>
                                        {field.required ? (
                                            <Tag bg="#dc2626" color="white" fontSize="xs">Yes</Tag>
                                        ) : (
                                            <Tag bg="white" color="black" border="1px solid #EDF2F7" fontSize="xs">No</Tag>
                                        )}
                                    </Td>
                                    <Td color="black" py={4} whiteSpace="normal" wordBreak="break-word">
                                        {field.description}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Response Section */}
            <Box mb="20px">
                <Text fontSize="20px" fontWeight="600" color="#000000" mb="10px" >
                    Response
                </Text>
                <Divider my={2} />

                {/* Success Response */}
                <Box mb="15px">
                    <Text fontSize="16px" fontWeight="500" mb="10px" >
                        Success Response (200 OK)
                    </Text>
                    <CodeBlock code={smsSuccessResponse} section="sms-success" />
                </Box>

                {/* Error Response */}
                {/* <Box>
                    <Text fontSize="16px" fontWeight="500" mb="10px" >
                        Error Response (4xx/5xx)
                    </Text>
                    <CodeBlock code={smsErrorResponse} section="sms-error" />
                </Box> */}
            </Box>

            {/* API Response Codes Section */}
            <Box mb="20px">
                <Text fontSize="20px" fontWeight="600" color="#000000" mb="10px" >
                    API Response Codes
                </Text>
                <Divider my={2} />
                <TableContainer
                    width="100%"
                    bg="white"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    overflow="hidden"
                >
                    <Table variant="simple" width="100%">
                        <Thead bg="#f7f7f7">
                            <Tr>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Code
                                </Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Status
                                </Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Description
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody fontSize="14px">
                            {responseCodes.map((item, index) => (
                                <Tr key={index} _hover={{ bg: "gray.50" }}>
                                    <Td color="black" fontWeight="500" py={4} >
                                        {item.code}
                                    </Td>
                                    <Td py={4}>
                                        <Tag
                                            bg={item.color === 'green' ? '#22c55e' : item.color === 'red' ? '#dc2626' : '#f97316'}
                                            color="white"
                                            fontSize="xs"
                                        >
                                            {item.status}
                                        </Tag>
                                    </Td>
                                    <Td color="black" py={4} whiteSpace="normal" wordBreak="break-word">
                                        {item.description}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Other Information Section */}
            <Box mb="20px">
                <Text fontSize="20px" fontWeight="600" color="#000000" mb="10px" >
                    Other Information
                </Text>
                <Divider my={2} />
                <TableContainer
                    width="100%"
                    bg="white"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    overflow="hidden"
                >
                    <Table variant="simple" width="100%">
                        <Thead bg="#f7f7f7">
                            <Tr>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Key
                                </Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none" letterSpacing="normal" py={4}>
                                    Description
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody fontSize="14px">
                            {otherInfo.map((item, index) => (
                                <Tr key={index} _hover={{ bg: "gray.50" }}>
                                    <Td color="black" fontWeight="500" py={4} >
                                        {item.key}
                                    </Td>
                                    <Td color="black" py={4} whiteSpace="normal" wordBreak="break-word">
                                        {item.description}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Notes Section */}
            <Box mb="30px">
                <Text fontSize="16px" fontWeight="600" color="#000000" mb="10px" >
                    Notes
                </Text>
                <Divider my={2} />
                <Box bg="#eaf3fdeb" borderRadius="lg" p={4} border="1px solid" borderColor="var(--brand-tint-strong)">
                    <VStack align="stretch" spacing={3}>
                        {notes.map((note, index) => (
                            <HStack key={index} align="flex-start" spacing={3}>
                                <Box w="8px" h="8px" bg="var(--brand-primary)" borderRadius="50%" mt="6px" flexShrink={0} />
                                <Text fontSize="12px" fontWeight="400" color="#000000" lineHeight="1.6" >
                                    {note}
                                </Text>
                            </HStack>
                        ))}
                    </VStack>
                </Box>
            </Box>
        </Box>
    );
};

export default SMSOnlyAPIPanel;